import React from "react";
import moment from "moment-timezone";

/**
 * Provides a collection of filter methods for table data.
 * @returns {Object} An object containing multiple filter methods.
 */
export const filterMethods = () => {
  /**
   * Resolves the appropriate filter ID from a filter object.
   * @param {Object} filter - The filter object containing potential IDs.
   * @returns {string} The resolved ID to be used for filtering.
   */
  const resolveFilterId = (filter) => filter.pivotId || filter.id;

  /**
   * Filters rows based on whether the column value starts with the filter value, case-insensitive.
   * @param {Object} filter - The filter object containing the filter value.
   * @param {Object} row - The data object for the current row.
   * @returns {boolean} True if the column value starts with the filter value, false otherwise.
   */
  const startsWithFilterMethod = (filter, row) => {
    const id = resolveFilterId(filter);
    if (!row[id]) return true;

    const cellValue = String(row[id]).toLowerCase();

    return cellValue.startsWith(filter.value.toLowerCase());
  };

  /**
   * Filters rows based on exact match with the filter value.
   * @param {Object} filter - The filter object specifying the value to match.
   * @param {Object} row - The data object for the current row.
   * @returns {boolean} True if the row's value exactly matches the filter value, false otherwise.
   */
  const exactMatchFilterMethod = (filter, row) => {
    if (filter.value === "all") return true;

    const id = resolveFilterId(filter);
    return row[id] === filter.value;
  };

  /**
   * Filters rows based on binary conditions (yes/no).
   * @param {Object} filter - The filter object specifying 'yes', 'no', or 'all'.
   * @param {Object} row - The data object for the current row.
   * @returns {boolean} True if the condition is met, false otherwise.
   */
  const binaryFilterMethod = (filter, row) => {
    if (filter.value === "all") return true;

    const id = resolveFilterId(filter);

    if (filter.value === "yes") return row[id];
    if (filter.value === "no") return !row[id];
  };

  return { startsWithFilterMethod, exactMatchFilterMethod, binaryFilterMethod };
};

/**
 * Provides a collection of cell rendering methods for displaying data in table cells.
 * @returns {Object} An object containing functions for rendering table cell content.
 */
export const cellRenders = () => {
  /**
   * Renders a binary state cell with 'Yes' or 'No'.
   * @param {Object} props - The props containing the cell value.
   * @returns {string} 'Yes', 'No', or 'N/A' based on the cell value.
   */
  const binaryCell = (props) => {
    if (props.value == undefined) return "N/A";
    return props.value ? "Yes" : "No";
  };

  /**
   * Creates a function that renders the label of a list item based on its value.
   * @param {Array} options - Array of options, each having 'value' and 'label'.
   * @returns {Function} A function that takes props and returns a string label.
   */
  const listValueCell = (options) => (props) => {
    const item = options.find((option) => option.value === props.value);
    return item ? item.label : "";
  };

  /**
   * Renders a date cell formatted to Eastern Time Zone.
   * @param {Object} props - The props containing the cell value.
   * @param {string|Date} props.value - The date value to be formatted.
   * @returns {string} The formatted date string in "MMMM, Do YYYY, h:mm A z" format.
   */
  const easternTzDateCell = (props) => {
    return moment(props.value)
      .tz("America/New_York")
      .format("MM/DD/YYYY, h:mm A z");
  };

  return { binaryCell, listValueCell, easternTzDateCell };
};

/**
 * Provides a collection of filter components suitable for use in React tables.
 * These components allow users to filter table data based on different criteria.
 * @returns {Object} An object containing React components for filtering.
 */
export const filterComponents = () => {
  /**
   * Renders a dropdown filter for binary state columns.
   * @param {Object} filter - The current filter settings.
   * @param {Function} onChange - The handler to call when the filter value changes.
   * @returns {JSX.Element} A select element for binary filtering.
   */
  const BinaryFilter = ({ filter, onChange }) => (
    <select
      onChange={(event) => onChange(event.target.value)}
      style={{ width: "100%" }}
      value={filter ? filter.value : "all"}
    >
      <option value="all">Show All</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );

  /**
   * Creates a component that renders a select element for filtering based on a list of options.
   * This higher-order function takes an array of options and returns a functional component.
   * @param {Array} options - Array of options for the select element, each having 'value' and 'label'.
   * @returns {Function} A React functional component that takes props containing the filter and change handler,
   *                     and returns a JSX.Element representing a select dropdown for filtering.
   */
  const ListFilter =
    (options) =>
    ({ filter, onChange }) =>
      (
        <select
          onChange={(event) => onChange(event.target.value)}
          style={{ width: "100%" }}
          value={filter ? filter.value : "all"}
        >
          <option value="all">Show All</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );

  return { BinaryFilter, ListFilter };
};
